import { useState } from "react";
import { View, Text, Image, Platform } from "react-native";
import { ChatwootSheet } from "@louise/chatwoot";
import { useTranslations } from "@src/packages/i18n";
import { useNotificationDeliverability } from "@src/packages/notifications";
import { useRequestAccountDeactivation } from "@src/packages/profile/hooks/useRequestAccountDeactivation";
import { EMBER } from "../../../../onboarding/theme";
import { useStytch } from "../../../../utils/useStytch";
import {
  ActionBanner,
  ActionRowCard,
  CardSubtitleText,
  CardSurface,
  CardTitleText,
  Dialog,
  DialogActions,
  DialogBodyText,
  DialogIcon,
  DialogTitleText,
  SecondaryButton,
  TextField,
} from "@louise/moon-ui";

const trashIcon =
  require("../../../../assets/images/you-icons/trash-bin.png") as number;
const alertIcon =
  require("../../../../assets/images/you-icons/alert.png") as number;
const envelopeIcon =
  require("../../../../assets/images/you-icons/envelope.png") as number;

type Props = {
  visible: boolean;
  confirmText: string;
  onChangeText: (text: string) => void;
  onClose: () => void;
};

export default function DeleteAccountModal({
  visible,
  confirmText,
  onChangeText,
  onClose,
}: Props) {
  const t = useTranslations();
  const stytch = useStytch();
  const { revokeRegisteredDevice } = useNotificationDeliverability();
  const requestDeactivation = useRequestAccountDeactivation();
  const confirmationToken = t("you.modals.delete_account.confirmation_token");
  const isEnabled = confirmText === confirmationToken;
  const [supportVisible, setSupportVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setError(null);
    try {
      await revokeRegisteredDevice();
    } catch {
      // Account deactivation remains available if device cleanup is offline;
      // the backend account lifecycle also removes its device registrations.
    }
    try {
      await requestDeactivation.mutateAsync();
    } catch {
      setError(t("you.modals.delete_account.error"));
      return;
    }
    try {
      // Sign out locally. The backend has disabled account access, and
      // `forceClear` ensures the local user/session is removed even if the
      // revoke network call cannot complete. RootNavigator then replaces the
      // authenticated Profile stack with its unauthenticated stack, whose
      // initial route is Onboarding. Resetting to Onboarding before this revoke
      // races WelcomeGoals' authenticated-user guard, which redirects to Home.
      await stytch.session.revoke({ forceClear: true });
    } catch {
      // no-op: account access was already deactivated server-side
    }
  };

  return (
    <>
      <Dialog
        visible={visible}
        onClose={onClose}
        layout="centered"
        contentStyle={{ paddingTop: 28, paddingBottom: 24 }}
      >
        <DialogIcon
          testID="delete-account-trash-icon"
          source={trashIcon}
          width={64}
          height={72}
        />
        <View accessibilityRole="header" style={{ marginTop: 12 }}>
          <DialogTitleText style={{ textAlign: "center" }}>
            {t("you.modals.delete_account.title")}
          </DialogTitleText>
        </View>
        <DialogBodyText style={{ textAlign: "center", marginBottom: 14 }}>
          {t("you.modals.delete_account.message")}
        </DialogBodyText>

        {/* Warning callout — compact because the lead copy already gives the
          account-level consequence. */}
        <CardSurface
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 10,
            padding: 12,
            marginBottom: 10,
          }}
        >
          <Image
            source={alertIcon}
            style={{
              width: 36,
              height: 36,
              ...(Platform.OS === "web" && { mixBlendMode: "overlay" }),
            }}
            resizeMode="contain"
          />
          <View style={{ flex: 1 }}>
            <CardTitleText>
              {t("you.modals.delete_account.deactivation_title")}
            </CardTitleText>
            <CardSubtitleText>
              {t("you.modals.delete_account.deactivation_description")}
            </CardSubtitleText>
          </View>
        </CardSurface>

        {/* Soft escape hatch — visually secondary to the confirmation flow. */}
        <ActionRowCard
          leading={
            <Image
              source={envelopeIcon}
              style={{
                width: 36,
                height: 36,
                ...(Platform.OS === "web" && { mixBlendMode: "overlay" }),
              }}
              resizeMode="contain"
            />
          }
          title={t("you.modals.delete_account.reactivation_title")}
          subtitle={t("you.modals.delete_account.reactivation_description")}
          onPress={() => setSupportVisible(true)}
          action="you.modals.delete_account.reactivation_title"
          style={{ marginBottom: 14 }}
        />

        {/* Explicitly name the account-state change: this is not a permanent
          data deletion. */}
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            fontWeight: "500",
            color: EMBER.colors.textLight,
            marginBottom: 8,
          }}
        >
          {t("you.modals.delete_account.type_prefix")}{" "}
          <Text style={{ fontWeight: "700", color: EMBER.colors.text }}>
            {confirmationToken}
          </Text>{" "}
          {t("you.modals.delete_account.type_suffix")}
        </Text>
        <TextField
          value={confirmText}
          onChangeText={onChangeText}
          placeholder={t("you.modals.delete_account.type_placeholder")}
          accessibilityLabel={t("you.modals.delete_account.type_placeholder")}
          autoCapitalize="characters"
          autoCorrect={false}
          style={{ marginBottom: 16 }}
        />

        <DialogActions testID="delete-account-actions" style={{ marginTop: 0 }}>
          <SecondaryButton
            label={t("you.modals.delete_account.cancel")}
            onPress={onClose}
            flex={1}
          />
          <SecondaryButton
            variant="destructive"
            label={t("you.modals.delete_account.confirm")}
            onPress={handleConfirm}
            disabled={!isEnabled}
            loading={requestDeactivation.isPending}
            flex={1}
          />
        </DialogActions>
        {error ? (
          <ActionBanner
            variant="important"
            message={error}
            style={{ marginTop: 12 }}
          />
        ) : null}
      </Dialog>
      <ChatwootSheet
        visible={supportVisible}
        onClose={() => setSupportVisible(false)}
      />
    </>
  );
}
